import FilterSection from "@/components/Marketplace/FilterSection";
import { useLoggedInUser } from "@/components/providers/ApiProvider/hooks";
import { LoadMoreButton } from "@/components/ui/buttons/LoadMoreButton";
import ContainerWrapper from "@/components/ui/ContainerWrapper";
import TrackRow from "@/components/ui/TrackRow";
import { hasActiveSession } from "@/lib/auth";
import { useSavedTracksInfiniteList } from "@/lib/lutely-api/hooks";
import { saveRedirect } from "@/lib/storage-utils";
import { Flex, Text, TextField } from "@radix-ui/themes";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { useDebounceValue } from "usehooks-ts";
import { noindexHead } from "@/lib/seo";

export const Route = createFileRoute("/account/liked-songs")({
  component: LikedSongs,
  head: () => noindexHead("Liked Songs | Lutely"),
  beforeLoad: async ({ location }) => {
    const isAuthenticated = await hasActiveSession();
    if (!isAuthenticated) {
      saveRedirect(location.href);
      throw redirect({
        to: "/",
        search: { auth: "login" as const },
      });
    }
  },
});

function LikedSongs() {
  const { data: user } = useLoggedInUser();
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounceValue(search, 300);

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useSavedTracksInfiniteList(
      { search: debouncedSearch || undefined },
      { enabled: !!user },
    );

  const tracks = data?.pages.flatMap((page) => page.content ?? []) ?? [];
  const isFiltered = !!debouncedSearch;

  return (
    <ContainerWrapper>
      <Flex className="pt-[25px]">
        <FilterSection />
        <Flex direction="column" className="pl-0 lg:pl-[50px] flex-1 min-w-0">
          <Text className="font-bold text-[25px]">Liked Songs</Text>

          <Flex className="mt-[20px] flex-wrap items-center gap-3">
            <TextField.Root
              placeholder="Search liked songs..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full sm:w-[280px]"
            />
          </Flex>

          <Flex direction="column" className="w-full mt-[20px]">
            {!isLoading && tracks.length === 0 ? (
              <Text className="text-text-secondary text-[14px] py-8 text-center">
                {isFiltered
                  ? "No liked songs match your search."
                  : "No liked songs yet — tap the heart on any track to save it here."}
              </Text>
            ) : (
              tracks.map((track) => (
                <TrackRow key={track.trackId} track={track} />
              ))
            )}
            {tracks.length > 0 && (
              <LoadMoreButton
                isLoading={isFetchingNextPage}
                hasMorePages={!!hasNextPage}
                showNoMoreItems={false}
                onLoadMore={() => fetchNextPage()}
              />
            )}
          </Flex>
        </Flex>
      </Flex>
    </ContainerWrapper>
  );
}
